import { useCallback, useEffect, useRef, useState } from 'react'
import { useBodyScrollLock, useFocusLock } from '@fs/zion-ui/quarks-authorized-use-only'

function hasOtherOpenModal(card) {
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some((modal) => !card?.contains(modal))
}

// Nowhere useful: dropped to the body when the focused control was removed, or still on the
// hidden card.
function isFocusLost(card) {
  const { activeElement } = document
  return !activeElement || activeElement === document.body || Boolean(card?.contains(activeElement))
}

/**
 * The card's open/closed state and everything that has to follow it: focus
 * trapped inside while open and moved to Close on opening, the page behind it
 * held still, Escape closing it, and focus handed back to the handle
 * afterwards - unless a row was chosen, in which case the new screen owns
 * focus and the handle must not steal it.
 *
 * @returns {{ isOpen: boolean, open: function, close: function, triggerRef, cardRef, closeRef }}
 *   `close(restoreFocus = true)`; pass false when navigating away.
 */
export default function useNavigationCard() {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef(null)
  const cardRef = useRef(null)
  const closeRef = useRef(null)
  const restoreFocusRef = useRef(false)

  useFocusLock({ enabled: isOpen, containerRef: cardRef })
  useBodyScrollLock(isOpen, cardRef)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback((restoreFocus = true) => {
    restoreFocusRef.current = restoreFocus
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (isOpen) {
      closeRef.current?.focus()
      return undefined
    }
    if (!restoreFocusRef.current) return undefined

    restoreFocusRef.current = false
    let waitForModal = null
    const focusTrigger = () => triggerRef.current?.focus()
    // After the closing frame, so focus lands on the handle rather than on a
    // card that is still being hidden.
    const restore = window.setTimeout(() => {
      if (!hasOtherOpenModal(cardRef.current)) {
        focusTrigger()
        return
      }
      // A row whose action opened a modal (a "leave these cards?" confirm) hands focus to that
      // modal. Pulling it back to the handle now would fight the modal's own focus lock, and the
      // two bounce focus between them until the stack overflows. So hand focus back once the
      // modal is gone instead - but only if nothing else took it, since a confirm that went on to
      // navigate may have put focus where the new screen wants it.
      waitForModal = new MutationObserver(() => {
        if (hasOtherOpenModal(cardRef.current)) return
        waitForModal.disconnect()
        if (isFocusLost(cardRef.current)) focusTrigger()
      })
      waitForModal.observe(document.body, { childList: true, subtree: true, attributeFilter: ['aria-modal'] })
    }, 0)
    return () => {
      window.clearTimeout(restore)
      waitForModal?.disconnect()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [close, isOpen])

  return { isOpen, open, close, triggerRef, cardRef, closeRef }
}
